'use client';

import type { WorkspaceRoleListItemDto } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { toast } from 'sonner';

import { mapRoleApiError } from '@/core/authz/map-role-api-error';
import { useDeleteRole } from '@/core/hooks/use-workspace-roles';
import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

export type DeleteRoleDialogDict = {
  title: string;
  description: string;
  inUseDescription: string;
  confirm: string;
  deleting: string;
  cancel: string;
  success: string;
  errors: {
    generic: string;
    roleNameAlreadyExists: string;
    invalidRoleName: string;
    cannotModifySystemRole: string;
    cannotDeleteSystemRole: string;
    roleInUse: string;
    permissionEscalation: string;
    forbiddenPermissions: string;
    invalidPermissions: string;
    entitlementRequired: string;
  };
};

interface DeleteRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  role: WorkspaceRoleListItemDto | null;
  roleDisplayName: string;
  memberCount: number;
  dict: DeleteRoleDialogDict;
  onSuccess: () => void;
}

export function DeleteRoleDialog({
  open,
  onOpenChange,
  workspaceId,
  role,
  roleDisplayName,
  memberCount,
  dict,
  onSuccess,
}: DeleteRoleDialogProps) {
  const deleteRole = useDeleteRole(workspaceId);
  const inUse = memberCount > 0;

  const handleDelete = async () => {
    if (!role || inUse) return;

    try {
      await deleteRole.mutateAsync(role.id);
      toast.success(dict.success);
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Failed to delete role:', error);
      toast.error(mapRoleApiError(error, dict.errors));
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        void triggerTapHaptic();
        onOpenChange(next);
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-[480px]">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{dict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {inUse
              ? dict.inUseDescription
                  .replace('{name}', roleDisplayName)
                  .replace('{count}', String(memberCount))
              : dict.description.replace('{name}', roleDisplayName)}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <ResponsiveDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteRole.isPending}
          >
            {dict.cancel}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteRole.isPending || inUse}
          >
            {deleteRole.isPending ? dict.deleting : dict.confirm}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
